'use client';

import { createClient } from '@/lib/supabase/client';

export interface SiteSetting {
  id: string;
  settingKey: string;
  settingValue: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export const logoService = {
  async getLogoUrl(): Promise<string | null> {
    const supabase = createClient();
    
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'site_logo_url')
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return data?.setting_value || null;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async uploadLogo(file: File): Promise<string | null> {
    const supabase = createClient();
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;

      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        if (isSchemaError(uploadError)) {
          console.error('Schema error:', uploadError.message);
          throw uploadError;
        } else {
          console.log('Upload error:', uploadError.message);
          return null;
        }
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // Update site_settings table
      const { error: updateError } = await supabase
        .from('site_settings')
        .upsert({
          setting_key: 'site_logo_url',
          setting_value: publicUrl,
          updated_by: user.id
        }, { onConflict: 'setting_key' });

      if (updateError) {
        if (isSchemaError(updateError)) {
          console.error('Schema error:', updateError.message);
          throw updateError;
        } else {
          console.log('Update error:', updateError.message);
          return null;
        }
      }

      return publicUrl;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateLogoUrl(logoUrl: string): Promise<boolean> {
    const supabase = createClient();
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert({
          setting_key: 'site_logo_url',
          setting_value: logoUrl,
          updated_by: user.id
        }, { onConflict: 'setting_key' });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Update error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  }
};
